import { useMemo, useRef, useState } from "react";
import { parseColor } from "@/document/model/color";
import type { CurvaDocument } from "@/document/model/document";
import { createSolidPaint, type Paint } from "@/document/model/styles";
import type { ColorSamplerRequestWithCommit } from "../colorSampler";
import { ColorPickerPopover } from "./ColorPickerPopover";
import { Metric } from "./Metric";
import { PaintColorInput } from "./PaintColorInput";
import { PanelSection } from "./PanelSection";
import { opacityForPaint, paintPreviewBackground, paintValueLabel, paintWithOpacity } from "./paintStyles";
import type { PickerMode } from "./types";
import {
  collectSelectionFillPaints,
  collectSelectionScopeNodes,
  collectSelectionStrokePaints,
  mergePaintsByIdentity,
  paintKey,
  selectionPaintRowKey,
  showsSelectionColorsSection,
  type SelectionColorScope,
} from "./selectionColors";

interface SelectionColorsSectionProps {
  document: CurvaDocument;
  selectedNodeIds: string[];
  activeColorSamplerSourceId?: string;
  onReplacePaint: (scope: SelectionColorScope, fromPaint: Paint, toPaint: Paint) => void;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onColorSampleCancel: (sourceId?: string) => void;
}

type SelectionPickerTarget = {
  scope: SelectionColorScope;
  fromPaint: Paint;
};

export function SelectionColorsSection({
  document,
  selectedNodeIds,
  activeColorSamplerSourceId,
  onReplacePaint,
  onColorSampleRequest,
  onColorSampleModeChange,
  onColorSampleCancel,
}: SelectionColorsSectionProps) {
  const [pickerTarget, setPickerTarget] = useState<SelectionPickerTarget | undefined>(undefined);
  const { fillPaints, strokePaints, allPaints } = useMemo(() => {
    const scopeNodes = collectSelectionScopeNodes(document, selectedNodeIds);
    const fills = collectSelectionFillPaints(scopeNodes);
    const strokes = collectSelectionStrokePaints(scopeNodes);
    return {
      fillPaints: fills,
      strokePaints: strokes,
      allPaints: mergePaintsByIdentity(fills, strokes),
    };
  }, [document, document.revision, selectedNodeIds]);

  if (!showsSelectionColorsSection(selectedNodeIds, document)) {
    return null;
  }

  if (fillPaints.length === 0 && strokePaints.length === 0 && allPaints.length === 0) {
    return null;
  }

  return (
    <>
      <PanelSection title="Selection colors">
        <div className="space-y-3">
          {fillPaints.length > 0 ? (
            <SelectionPaintSubpanel
              scope="fill"
              title="Fill"
              paints={fillPaints}
              onPaintCommit={(fromPaint, toPaint) => onReplacePaint("fill", fromPaint, toPaint)}
              onColorClick={fromPaint => setPickerTarget({ scope: "fill", fromPaint })}
            />
          ) : null}
          {strokePaints.length > 0 ? (
            <SelectionPaintSubpanel
              scope="stroke"
              title="Stroke"
              paints={strokePaints}
              onPaintCommit={(fromPaint, toPaint) => onReplacePaint("stroke", fromPaint, toPaint)}
              onColorClick={fromPaint => setPickerTarget({ scope: "stroke", fromPaint })}
            />
          ) : null}
          {allPaints.length > 0 ? (
            <SelectionPaintSubpanel
              scope="all"
              title="All"
              paints={allPaints}
              onPaintCommit={(fromPaint, toPaint) => onReplacePaint("all", fromPaint, toPaint)}
              onColorClick={fromPaint => setPickerTarget({ scope: "all", fromPaint })}
            />
          ) : null}
        </div>
      </PanelSection>

      {pickerTarget ? (
        <ColorPickerPopover
          title={pickerTarget.scope === "fill" ? "Fill" : pickerTarget.scope === "stroke" ? "Stroke" : "Color"}
          paint={pickerTarget.fromPaint}
          onClose={() => {
            setPickerTarget(undefined);
            onColorSampleCancel(`selection-color-${paintKey(pickerTarget.fromPaint)}`);
          }}
          activeColorSamplerSourceId={activeColorSamplerSourceId}
          onColorSampleRequest={onColorSampleRequest}
          onColorSampleModeChange={onColorSampleModeChange}
          onChange={paint => onReplacePaint(pickerTarget.scope, pickerTarget.fromPaint, paint)}
        />
      ) : null}
    </>
  );
}

function SelectionPaintSubpanel({
  scope,
  title,
  paints,
  onPaintCommit,
  onColorClick,
}: {
  scope: SelectionColorScope;
  title: string;
  paints: Paint[];
  onPaintCommit: (fromPaint: Paint, toPaint: Paint) => void;
  onColorClick: (fromPaint: Paint) => void;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] uppercase text-slate-500">{title}</div>
      <div className="space-y-1">
        {paints.map(paint => (
          <SelectionPaintRow
            key={`${scope}-${selectionPaintRowKey(paint)}`}
            paint={paint}
            onColorClick={() => onColorClick(paint)}
            onPaintCommit={onPaintCommit}
          />
        ))}
      </div>
    </div>
  );
}

function SelectionPaintRow({
  paint,
  onColorClick,
  onPaintCommit,
}: {
  paint: Paint;
  onColorClick: () => void;
  onPaintCommit: (fromPaint: Paint, toPaint: Paint) => void;
}) {
  const paintRef = useRef(paint);
  paintRef.current = paint;

  const solidColor = paint.type === "solid" ? parseColor(paint.color) : undefined;
  const opacity = opacityForPaint(paint);
  const label = paintValueLabel(paint);

  function commitPaint(toPaint: Paint): void {
    onPaintCommit(paintRef.current, toPaint);
  }

  return (
    <div className="flex h-7 min-w-0 items-center gap-1.5 rounded bg-white/[0.04] px-1.5">
      <button
        type="button"
        aria-label="Edit color"
        onClick={onColorClick}
        className="size-5 shrink-0 rounded-sm border border-white/20"
        style={{ background: paintPreviewBackground(paint) }}
      />
      {solidColor ? (
        <PaintColorInput
          color={solidColor}
          onCommit={color => commitPaint(createSolidPaint(color))}
        />
      ) : (
        <button
          type="button"
          onClick={onColorClick}
          className="flex min-w-0 flex-1 overflow-hidden text-left"
        >
          <span className="min-w-0 truncate font-mono text-[11px] text-slate-200" title={label}>
            {label}
          </span>
        </button>
      )}
      <div className="shrink-0">
        <Metric
          label="%"
          value={opacity}
          disabled={false}
          compact
          min={0}
          max={100}
          onCommit={nextOpacity => {
            const nextPaint = paintWithOpacity(paintRef.current, nextOpacity);
            if (nextPaint) {
              commitPaint(nextPaint);
            }
          }}
        />
      </div>
    </div>
  );
}
